import { useState } from "react";
import GlowingEmailInput from "@/components/GlowingEmailInput";
import { WaitlistJoinedMessage } from "@/components/WaitlistJoinedMessage";
import { useWaitlist } from "@/context/WaitlistContext";
import { ArrowRight, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

export default function HeroSection({ api }) {
  const { joined, markJoined } = useWaitlist();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${api}/waitlist`, { email, source: "hero" });
      markJoined(email);
      setEmail("");
      toast.success("You are on the waitlist.");
    } catch (err) {
      if (err.response?.status === 409) {
        markJoined(email);
        toast.info("You are already on the waitlist.");
      } else {
        toast.error("Something went wrong. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[90dvh] sm:min-h-screen flex items-center justify-center overflow-hidden py-16 sm:py-12"
    >
      {/* Background image + overlay */}
      <div className="absolute inset-0">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/a964e757-17d0-42c6-9ae1-72dac48014e0/images/b09cdbcf256b28482400848b12c26b2ab2e10c4430944840173dfc8958823d10.png"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[hsl(270_22%_4%/0.82)]" />
      </div>

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}
      />

      {/* Ambient glows */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(90vw,520px)] h-[min(90vw,520px)] bg-violet-600/10 rounded-full blur-[100px] animate-pulse-glow pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[280px] h-[280px] bg-fuchsia-600/[0.06] rounded-full blur-[90px] pointer-events-none" />

      <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
        {/* Badge */}
        <div
          data-testid="hero-badge"
          className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-violet-500/10 border border-violet-400/25 text-xs tracking-[0.15em] uppercase text-violet-200/90 font-semibold mb-6 animate-fade-in"
        >
          <Sparkles className="w-3.5 h-3.5 text-violet-400" />
          Meet ReachMeNow
        </div>

        {/* Headline */}
        <h1
          data-testid="hero-headline"
          className="text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-bold leading-[1.05] mb-5 animate-fade-in-up"
        >
          Be reachable
          <br />
          <span className="bg-gradient-to-r from-violet-300 via-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
            Instantly
          </span>
        </h1>

        {/* Subheadline */}
        <p
          data-testid="hero-subheadline"
          className="text-base sm:text-lg text-white/60 max-w-xl mx-auto leading-relaxed mb-8 animate-fade-in-up"
          style={{ animationDelay: "0.15s" }}
        >
          Your AI front door answers fast, qualifies requests, and books calls
          so good leads land on your calendar without the back-and-forth.
        </p>

        {/* Email capture */}
        <div id="hero-email" className="animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
          {!joined ? (
            <form
              data-testid="hero-email-form"
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 max-w-lg mx-auto w-full"
              noValidate
            >
              <GlowingEmailInput
                data-testid="hero-email-input"
                name="email"
                autoComplete="email"
                inputMode="email"
                placeholder="you@company.com"
                aria-label="Email address for waitlist"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className="flex-1 min-w-0 w-full"
              />
              <button
                data-testid="hero-submit-button"
                type="submit"
                disabled={loading}
                aria-busy={loading}
                className="h-[52px] shrink-0 bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white font-medium rounded-full px-6 shadow-lg shadow-violet-600/30 hover:from-violet-400 hover:to-fuchsia-500 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 flex items-center gap-2 whitespace-nowrap w-full sm:w-auto justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {loading ? "Joining..." : "Get Early Access"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          ) : (
            <WaitlistJoinedMessage
              data-testid="hero-success-message"
              className="max-w-lg"
            />
          )}
          {!joined && (
            <p className="text-xs text-white/30 mt-4">No spam. Early access only.</p>
          )}
        </div>
      </div>
    </section>
  );
}
